import type { ConflictRecord, Entities, OpResult, PullResult, QueuedOp } from "./types";

/**
 * Единственное место, где слой данных встречается с сетью. Экраны сюда не заглядывают
 * никогда: их контракт — хуки чтения и `dispatch`.
 *
 * Интерфейс, а не конкретный клиент, — чтобы стор тестировался без сервера и чтобы
 * замена tRPC на что угодно не трогала ни стор, ни экраны.
 */
export type Transport = {
	pull(cursor: string | null): Promise<PullResult>;
	push(ops: QueuedOp[]): Promise<OpResult[]>;
	/** Таймзона системы: «сегодня» берётся из настройки, а НЕ из часов браузера. */
	settings(): Promise<{ tz: string }>;
};

export type PersistedMeta = {
	cursor: string | null;
	/** Отпечаток формы реплики; не совпал — реплику снести, очередь не трогать. */
	replicaFingerprint: string;
	/**
	 * Таймзона на момент последнего удачного пулла. Персистится потому, что оффлайн
	 * «сегодня» иначе считается по UTC — и листалка дат уезжает на сутки.
	 * Опциональна: старые записи `meta` читаются как есть, версию базы бампать незачем.
	 */
	tz?: string;
};

export type PersistedState = {
	meta: PersistedMeta;
	entities: Entities;
	queue: QueuedOp[];
	conflicts: ConflictRecord[];
};

/**
 * Персистентность разбита ПО СТОРАМ, а не одним `save(state)`.
 *
 * Причина в разной цене потери: очередь — это работа человека оффлайн, она пишется
 * сразу; реплика восстанавливается одним `pull`, её запись можно и придержать.
 * Общий `save` заставил бы писать двадцать тысяч нод на каждое нажатие клавиши.
 */
export type PersistenceDriver = {
	load(): Promise<PersistedState | null>;
	saveMeta(meta: PersistedMeta): Promise<void>;
	saveEntities(entities: Entities): Promise<void>;
	saveQueue(queue: QueuedOp[]): Promise<void>;
	saveConflicts(conflicts: ConflictRecord[]): Promise<void>;
	/** Снести ТОЛЬКО реплику и курсор. Очередь и карантин остаются. */
	clearReplica(): Promise<void>;
	clear(): Promise<void>;
};

/**
 * Драйвер «всё в памяти». Не заглушка на выброс: он же работает в приватном режиме,
 * когда IndexedDB недоступна, — тот же код, другой драйвер (offline-sync §12).
 */
export function memoryDriver(): PersistenceDriver {
	let state: PersistedState | null = null;

	const ensure = (): PersistedState => {
		state ??= {
			meta: { cursor: null, replicaFingerprint: "" },
			entities: { nodes: [], placements: [], links: [], completions: [] },
			queue: [],
			conflicts: []
		};
		return state;
	};

	return {
		load: async () => (state?.meta.replicaFingerprint ? state : null),
		saveMeta: async (meta) => {
			ensure().meta = meta;
		},
		saveEntities: async (entities) => {
			ensure().entities = entities;
		},
		saveQueue: async (queue) => {
			ensure().queue = queue;
		},
		saveConflicts: async (conflicts) => {
			ensure().conflicts = conflicts;
		},
		clearReplica: async () => {
			if (state) {
				state.meta = { cursor: null, replicaFingerprint: "" };
				state.entities = { nodes: [], placements: [], links: [], completions: [] };
			}
		},
		clear: async () => {
			state = null;
		}
	};
}
